<!-- Order Confirmation -->
@if (!submitted()) {
  <div class="order-confirmation">

    <!-- Header -->
    <div class="confirmation-header">
      <button class="back-btn" (click)="goBack()">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <h1>Realizar Pedido</h1>
      <div class="order-mode-badge">
        {{ orderModeIcon }} {{ orderModeText }}
      </div>
    </div>

    <!-- Restaurant Info -->
    @if (cart(); as currentCart) {
      <div class="restaurant-info">
        <h2>{{ currentCart.restaurant_name }}</h2>
        <small>Mesa: {{ currentCart.terminal_suffix }}</small>
      </div>
    }

    <!-- Collapsible: Resumen del pedido -->
    <div class="collapsible-section">
      <button class="collapsible-header" (click)="toggleSummary()">
        <span class="collapsible-title">Resumen del pedido</span>
        <span class="collapsible-meta">{{ totalItems() }} art. &middot; {{ formatPrice(totalPrice()) }}</span>
        <span class="collapsible-arrow" [class.open]="showSummary()">&#9660;</span>
      </button>
      @if (showSummary()) {
        <div class="collapsible-body">
          <div class="order-items">
            @for (item of cartItems(); track item.id) {
              <div class="order-item">
                <div class="item-info">
                  <div class="item-name">{{ item.name }}</div>
                  @if (item.customizations.length > 0) {
                    <div class="item-customizations">
                      {{ getCustomizationsText(item.customizations) }}
                    </div>
                  }
                  @if (item.notes) {
                    <div class="item-notes">{{ item.notes }}</div>
                  }
                  <div class="item-qty">
                    {{ formatPrice(item.price) }} x {{ item.quantity }}
                  </div>
                </div>
                <div class="item-total">
                  {{ formatPrice(getItemTotal(item)) }}
                </div>
              </div>
            }
          </div>
          <div class="order-totals">
            <div class="total-row">
              <span>Articulos:</span>
              <span>{{ totalItems() }}</span>
            </div>
            <div class="total-row grand-total">
              <span>Total:</span>
              <span>{{ formatPrice(totalPrice()) }}</span>
            </div>
          </div>
        </div>
      }
    </div>

    <!-- Collapsible: Alergenos -->
    <div class="collapsible-section">
      <button class="collapsible-header" (click)="toggleAllergens()">
        <span class="collapsible-title">Alergenos</span>
        <span class="collapsible-meta" *ngIf="selectedAllergens().length > 0">
          {{ selectedAllergens().length }} seleccionados
        </span>
        <span class="collapsible-arrow" [class.open]="showAllergens()">&#9660;</span>
      </button>
      @if (showAllergens()) {
        <div class="collapsible-body">
          <!-- Standard allergens chips -->
          <div class="allergen-chips">
            <button
              *ngFor="let a of standardAllergens"
              class="allergen-chip"
              [class.selected]="selectedAllergens().includes(a)"
              (click)="toggleAllergen(a)">
              {{ a }}
            </button>
          </div>

          <!-- Custom allergens already added -->
          <div class="custom-allergens" *ngIf="selectedAllergens().length > 0">
            <div class="selected-allergens-list">
              <span
                *ngFor="let a of selectedAllergens()"
                class="selected-allergen-tag">
                {{ a }}
                <button class="tag-remove" (click)="removeAllergen(a)">&times;</button>
              </span>
            </div>
          </div>

          <!-- Add custom allergen -->
          <div class="add-allergen-row">
            <input
              type="text"
              [(ngModel)]="customAllergen"
              placeholder="Otro alergeno..."
              class="allergen-input"
              maxlength="50"
              (keyup.enter)="addCustomAllergen()"
            />
            <button class="btn-add-allergen" (click)="addCustomAllergen()" [disabled]="!customAllergen.trim()">
              Anadir
            </button>
          </div>

          <!-- Special notes -->
          <div class="notes-group">
            <label>Notas especiales</label>
            <textarea
              [(ngModel)]="specialNotes"
              placeholder="Indicaciones adicionales..."
              class="form-textarea"
              rows="2"
            ></textarea>
          </div>
        </div>
      }
    </div>

    <!-- Collapsible: Datos del cliente (read-only, grayed out for dine-in) -->
    <div class="collapsible-section muted">
      <button class="collapsible-header" (click)="toggleCustomerData()">
        <span class="collapsible-title">Datos del cliente</span>
        <span class="collapsible-meta">Se envian automaticamente</span>
        <span class="collapsible-arrow" [class.open]="showCustomerData()">&#9660;</span>
      </button>
      @if (showCustomerData()) {
        <div class="collapsible-body">
          <div class="customer-readonly">
            <div class="readonly-row">
              <span class="readonly-label">Nombre</span>
              <span class="readonly-value">{{ customerName || 'No disponible' }}</span>
            </div>
            <div class="readonly-row" *ngIf="customerPhone">
              <span class="readonly-label">Telefono</span>
              <span class="readonly-value">{{ customerPhone }}</span>
            </div>
          </div>
          <p class="customer-hint">Los datos se envian automaticamente con tu cuenta.</p>
        </div>
      }
    </div>

    <!-- Notification Permission -->
    @if (!notificationsEnabled() && !notificationsDenied()) {
      <div class="notification-banner">
        <div class="notification-banner-content">
          <span class="notification-banner-icon">&#128276;</span>
          <div class="notification-banner-text">
            <strong>Activa las notificaciones</strong>
            <span>Para recibir avisos del estado de tu pedido</span>
          </div>
        </div>
        <button class="btn-enable-notif" (click)="enableNotifications()">Activar</button>
      </div>
    }
    @if (notificationsDenied()) {
      <div class="notification-banner warning">
        <span class="notification-banner-icon">&#9888;&#65039;</span>
        <span class="notification-banner-text">
          <strong>No podras recibir avisos del estado de tu pedido.</strong>
          Activa las notificaciones desde los ajustes del navegador.
        </span>
      </div>
    }

    <!-- Error Message -->
    @if (errorMessage()) {
      <div class="error-banner">
        {{ errorMessage() }}
      </div>
    }

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button class="btn-cancel" (click)="goBack()" [disabled]="submitting()">
        Volver
      </button>
      <button
        class="btn-confirm"
        (click)="submitOrder()"
        [disabled]="submitting()">
        @if (submitting()) {
          <span class="spinner-inline"></span>
          Enviando...
        } @else {
          Confirmar Pedido
        }
      </button>
    </div>
  </div>
}

<!-- Order Success -->
@if (submitted()) {
  <div class="order-success">
    <div class="success-icon">&#10003;</div>
    <h1>Pedido enviado</h1>

    @if (submittedOrderInfo(); as info) {
      <div class="pending-validation">
        Pendiente de validacion por <strong>{{ info.restaurantName }}</strong>
      </div>
    }

    @if (orderId()) {
      <div class="order-id">
        Pedido #{{ orderId() }}
      </div>
    }

    <div class="success-info">
      @if (submittedOrderInfo(); as info) {
        <p>{{ orderModeIcon }} {{ orderModeText }}</p>
        <p>Total: {{ formatPrice(info.total) }}</p>
      }
    </div>

    <p class="success-hint">Recibiras una notificacion cuando el local confirme tu pedido.</p>

    <button class="btn-dashboard" (click)="goToDashboard()">
      Ir al inicio
    </button>
  </div>
}
